/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-15 10:31:44
 **/

import { create } from "zustand/react";
import { createJSONStorage, persist, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

/**
 * Represents the states and settings for the device manager.
 *
 * This type is used to track and manage the settings of media devices,
 * including camera, microphone, and speaker configurations, as well as the
 * hydration state of the device manager.
 *
 * Properties:
 * - `camera` - Represents the current setting for the camera device. This includes detailed configurations and any specific state info related to the camera.
 * - `microphone` - Represents the current setting for the microphone device. This includes detailed configurations and any specific state info related to the microphone.
 * - `speaker` - Represents the current setting for the speaker device. This includes detailed configurations and any specific state info related to the speaker.
 * - `has_hydrated` - A boolean indicating whether the device manager has completed its initialization and state synchronization process.
 */
type DeviceManagerStates = {
	devices: Array<DeviceType.MediaDeviceSetting>;
	camera: DeviceType.MediaDeviceSetting;
	microphone: DeviceType.MediaDeviceSetting;
	speaker: DeviceType.MediaDeviceSetting;
	has_hydrated: boolean;
};

/**
 * Represents a set of actions that can be performed to manage device state and permissions.
 *
 * @typedef {Object} DeviceManagerActions
 *
 * @property {function(boolean): void} set_hydrated - Updates the hydration state of the device manager.
 * @property {function(DeviceManager.MediaDevicesName, PermissionState): void} set_permission - Assigns a specific permission state to a given device name.
 */
type DeviceManagerActions = {
	set_devices: (devices: Array<DeviceType.MediaDeviceSetting>) => void;
	set_hydrated: (state: boolean) => void;
	set_permission: (name: DeviceType.MediaDevicesName, permission: PermissionState) => void;
};

const DeviceManagerDefaultStates: DeviceManagerStates = {
	devices: [],
	camera: {},
	microphone: {},
	speaker: {},
	has_hydrated: false
};

const useDeviceManager = create(
	persist(
		subscribeWithSelector(
			immer<DeviceManagerStates & DeviceManagerActions>((set) => ({
				...DeviceManagerDefaultStates,
				set_devices: (devices) => set({ devices }),
				set_hydrated: (state: boolean) => set({ has_hydrated: state }),
				set_permission(name, permission) {
					set((state) => {
						state[name].permission = permission;
					});
				}
			}))
		),
		{
			name: "device-manager",
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				camera: state.camera,
				microphone: state.microphone,
				speaker: state.speaker
			}),
			onRehydrateStorage: (state) => {
				return () => {
					state.set_hydrated(true);
				};
			}
		}
	)
);

export default useDeviceManager;
